/**
 * QuestionsPage — orchestrator for the question browsing experience used by
 * admin-console and teacher-app.
 *
 * Owns:
 *   - filter state (current + applied)
 *   - selection / editing state
 *   - tag selection
 *   - data queries (question list + selected detail)
 *   - effects (initial-prop reset, search override, default subject,
 *     auto-select, single-pane-aware no-auto-select)
 *   - navigation callbacks (prev / next / back)
 *
 * Renders:
 *   - the split-layout wrapper (with `has-detail` for mobile)
 *   - `QuestionListPanel` (left)
 *   - `QuestionDetailPanel` (right, includes the read-only view + slots for
 *     `renderEditMode` / `renderCreateMode` injected by consumers)
 *
 * Split from a single ~1100-line file on 2026-05-17 to make the list and
 * detail/edit-form concerns independently editable. The public API
 * (`QuestionsPage`, `QuestionsPageProps`) is unchanged.
 */
import { useQuery } from '@tanstack/react-query';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useModules } from '../reference-data';
// `useResponsive`'s `isMobile` fires at ≤640px, but QuestionsPage.css
// single-panes at ≤900px (tablet portrait, narrow laptop windows). Those
// widths need the auto-select-first skip too — otherwise clicking Back
// re-opens the question. Use a dedicated 900px subscription instead. See
// the no-auto-select effect below.
const SINGLE_PANE_QUERY = '(max-width: 900px)';
const singlePaneMql = typeof window !== 'undefined'
    ? window.matchMedia(SINGLE_PANE_QUERY)
    : null;
import { flattenQuestionIds } from '../user-tags/types';
import { useUserTags } from '../user-tags/useUserTags';
import { SUBJECT_OPTIONS, QUESTION_SUBJECT_OPTIONS } from '@padcanvas/shared';
import type { QuestionsPageProps } from './types';
import type { ModuleItem } from '../reference-data/types';
import {
    EMPTY_FILTERS,
    buildApiFilters,
    filtersEqual,
    clearAdvancedFilters,
    type QuestionFilters,
} from './filterTypes';
import { QuestionListPanel } from './QuestionListPanel';
import { QuestionDetailPanel } from './QuestionDetailPanel';
import '../user-tags/TagFilter.css';
import './QuestionsPage.css';

export function QuestionsPage({
    api,
    isAdmin = false,
    allowedSubjects,
    initialSubject = '',
    initialModuleId = '',
    initialQuestionType = '',
    initialQuestionId,
    scopeQuestionIds,
    renderDetailActions,
    renderDetailLeftActions,
    renderEditMode,
    renderHeaderActions,
    renderSelectionBar,
    statusMessage,
    onClearStatus,
    onStatusClick,
    renderAboveFilters,
    onFiltersChange,
    renderDetailBadges,
    renderDetailExtra,
    renderBeforeMoreDetails,
    renderInMoreDetails,
    historyApi,
    isCreating,
    renderCreateMode,
    queryKeySuffix,
    searchOverride,
    resolveTeacherName,
    viewerId,
    showStatusFilter,
    hideCatalogueFilters,
    hideProvenance,
    relatedQuestionHref,
}: QuestionsPageProps) {
    // ── Filter state ──────────────────────────────────────────────────
    const initFilters: QuestionFilters = {
        ...EMPTY_FILTERS,
        subject: initialSubject,
        moduleId: initialModuleId,
        questionType: initialQuestionType,
        search: scopeQuestionIds?.length ? '' : (initialQuestionId || ''),
    };
    const [filters, setFilters] = useState<QuestionFilters>(initFilters);
    const [appliedFilters, setAppliedFilters] = useState<QuestionFilters>(initFilters);

    // Reset filters when initial props change (e.g. nav click)
    useEffect(() => {
        const next: QuestionFilters = {
            ...EMPTY_FILTERS,
            subject: initialSubject,
            moduleId: initialModuleId,
            questionType: initialQuestionType,
            search: scopeQuestionIds?.length ? '' : (initialQuestionId || ''),
        };
        setFilters(next);
        setAppliedFilters(next);
        if (!initialQuestionId) setSelectedQuestionId(null);
    }, [initialSubject, initialModuleId, initialQuestionType, initialQuestionId, scopeQuestionIds]);

    // Apply external search override (e.g. importBatchId after import)
    useEffect(() => {
        if (searchOverride !== undefined) {
            const next: QuestionFilters = { ...EMPTY_FILTERS, search: searchOverride };
            setFilters(next);
            setAppliedFilters(next);
        }
    }, [searchOverride]);

    const isDirty = !filtersEqual(filters, appliedFilters);

    const setFilter = useCallback(
        <K extends keyof QuestionFilters>(key: K, value: QuestionFilters[K]) => {
            setFilters(prev => {
                const next = { ...prev, [key]: value };
                if (key === 'subject') next.moduleId = '';
                return next;
            });
        },
        [],
    );

    // ── Tag filtering ──────────────────────────────────────────────────
    // Tag filter in the URL (?tags=a,b) so returning to the browser restores it.
    // Isolated from the subject/module filter model, which the consumers feed as
    // props — that's a separate, larger migration.
    const [tagSearchParams, setTagSearchParams] = useSearchParams();
    const [selectedTags, setSelectedTags] = useState<Set<string>>(
        () => new Set((tagSearchParams.get('tags') || '').split(',').filter(Boolean)),
    );
    useEffect(() => {
        setTagSearchParams(prev => {
            const p = new URLSearchParams(prev);
            if (selectedTags.size > 0) p.set('tags', [...selectedTags].join(','));
            else p.delete('tags');
            return p;
        }, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTags]);
    const { visibleTags } = useUserTags();
    const tagQuestionSets = useMemo(() => {
        const map = new Map<string, Set<string>>();
        for (const t of visibleTags) map.set(t.tag, flattenQuestionIds(t.questions));
        return map;
    }, [visibleTags]);
    const questionTagNames = useMemo(() => {
        const map = new Map<string, string[]>();
        for (const [tag, ids] of tagQuestionSets) {
            for (const id of ids) {
                if (!map.has(id)) map.set(id, []);
                map.get(id)!.push(tag);
            }
        }
        return map;
    }, [tagQuestionSets]);
    const allTagNames = useMemo(() => [...tagQuestionSets.keys()].sort(), [tagQuestionSets]);

    // ── Selection / detail / advanced-filter UI state ─────────────────
    // Seeded from the incoming id (deep link: "view this question in the
    // list"). Set at mount rather than in an effect — an effect lands in the
    // same commit as the select-first-question one below, which then reads a
    // still-null selection from its own closure and overwrites the target.
    const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(initialQuestionId ?? null);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isEditing, setIsEditing] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);

    // Single-pane (≤ 900px): list and detail swap full-screen rather than
    // sit side-by-side. CSS hides one or the other based on `has-detail`
    // on the root; the Back button below clears selection. `isSinglePane`
    // also disables the auto-select-first-question behavior so the list
    // (not a question detail) is the landing view on first load — without
    // this, clicking Back would re-open the same question because the
    // effect re-selects the first row. The 900px threshold is paired
    // verbatim with QuestionsPage.css's single-pane media query — `isMobile`
    // from useResponsive only fires at ≤ 640px and was missing iPad-portrait
    // and similar widths. See docs/FeatureDesigns/mobile-responsive-audit.md.
    const [isSinglePane, setIsSinglePane] = useState<boolean>(() => singlePaneMql?.matches ?? false);
    useEffect(() => {
        if (!singlePaneMql) return;
        const handler = () => setIsSinglePane(singlePaneMql.matches);
        singlePaneMql.addEventListener('change', handler);
        return () => singlePaneMql.removeEventListener('change', handler);
    }, []);
    const hasDetail = !!selectedQuestionId || !!isCreating;

    // ── Advanced-filter derived state ─────────────────────────────────
    const selectedDiagramLocations = useMemo(
        () => new Set(filters.hasDiagramIn ? filters.hasDiagramIn.split(',').filter(Boolean) : []),
        [filters.hasDiagramIn],
    );
    const toggleDiagramLocation = useCallback((loc: string) => {
        const next = new Set(selectedDiagramLocations);
        if (next.has(loc)) next.delete(loc); else next.add(loc);
        setFilter('hasDiagramIn', [...next].join(','));
    }, [selectedDiagramLocations, setFilter]);
    const handleClearAdvancedFilters = useCallback(() => {
        setFilters(prev => clearAdvancedFilters(prev));
    }, []);
    const advancedFilterCount = selectedDiagramLocations.size
        + (filters.enrichStatus ? 1 : 0)
        + (filters.readiness ? filters.readiness.split(',').filter(Boolean).length : 0)
        + (filters.dateAddedFrom || filters.dateAddedTo ? 1 : 0)
        + (filters.dateModifiedFrom || filters.dateModifiedTo ? 1 : 0)
        + (filters.appearedInExam ? filters.appearedInExam.split(',').filter(Boolean).length : 0)
        + (filters.appearedInYear ? 1 : 0)
        + (filters.appearedOnDate ? 1 : 0)
        + (filters.appearedInSession ? 1 : 0);

    // ── Modules ────────────────────────────────────────────────────────
    const { modules: allModules } = useModules();

    const modulesBySubject = useMemo(() => {
        const grouped: Record<string, ModuleItem[]> = {};
        for (const mod of allModules) {
            if (!grouped[mod.subject]) grouped[mod.subject] = [];
            grouped[mod.subject].push(mod);
        }
        return grouped;
    }, [allModules]);

    const modulesById = useMemo(() => {
        const map: Record<string, ModuleItem> = {};
        for (const mod of allModules) map[mod.id] = mod;
        return map;
    }, [allModules]);

    /** For topic-level modules, show "Chapter - Topic". For chapters, just the name. */
    const getModuleLabel = useCallback((moduleId: string) => {
        const mod = modulesById[moduleId];
        if (!mod) return undefined;
        if (mod.moduleLevel === 'topic' && mod.parentModuleId) {
            const parent = modulesById[mod.parentModuleId];
            if (parent) return `${parent.displayName} - ${mod.displayName}`;
        }
        return mod.displayName;
    }, [modulesById]);

    // ── Data queries ──────────────────────────────────────────────────
    // When exactly one tag is selected, use server-side filtering (handles cross-subject)
    const serverTag = selectedTags.size === 1 ? [...selectedTags][0] : undefined;
    const apiFilters = useMemo(
        () => {
            const base = buildApiFilters(appliedFilters, isAdmin, serverTag, showStatusFilter);
            // `ids` scopes the list server-side to exactly this set.
            return scopeQuestionIds?.length ? { ...base, ids: scopeQuestionIds.join(',') } : base;
        },
        [appliedFilters, isAdmin, serverTag, showStatusFilter, scopeQuestionIds],
    );

    useEffect(() => {
        onFiltersChange?.(apiFilters);
    }, [apiFilters, onFiltersChange]);

    const { data, isLoading, isFetching } = useQuery({
        queryKey: ['question-list', apiFilters, queryKeySuffix],
        queryFn: () => api.getQuestionList(apiFilters),
    });

    const { data: selectedQuestion, isLoading: isDetailLoading } = useQuery({
        queryKey: ['question-detail', selectedQuestionId],
        queryFn: () => api.getQuestionDetail(selectedQuestionId!),
        enabled: !!selectedQuestionId,
    });

    const filteredQuestions = useMemo(() => {
        const all = data?.questions || [];
        // Single tag → already filtered server-side; multi-tag → client-side OR filter
        if (selectedTags.size <= 1) return all;
        return all.filter(q => {
            for (const tag of selectedTags) {
                if (tagQuestionSets.get(tag)?.has(q.id)) return true;
            }
            return false;
        });
    }, [data, selectedTags, tagQuestionSets]);

    // ── Auto-selection ─────────────────────────────────────────────────
    const [didAutoSelect, setDidAutoSelect] = useState(!!initialQuestionId);
    useEffect(() => {
        if (initialQuestionId && !didAutoSelect && data) {
            setSelectedQuestionId(initialQuestionId);
            setDidAutoSelect(true);
        }
    }, [initialQuestionId, didAutoSelect, data]);

    // When filtered list changes, auto-select first question if current selection
    // is not in the list. Skipped in single-pane mode (≤900px) so the list is the
    // landing view AND so clicking Back doesn't immediately re-select; the user
    // picks which question to open.
    useEffect(() => {
        if (isSinglePane) return;
        if (filteredQuestions.length === 0) return;
        // A requested question that hasn't arrived in the list yet must not be
        // replaced by the first row — it lands a fetch later.
        if (initialQuestionId && !didAutoSelect) return;
        if (selectedQuestionId && filteredQuestions.some(q => q.id === selectedQuestionId)) return;
        setSelectedQuestionId(filteredQuestions[0].id);
        setIsEditing(false);
    }, [filteredQuestions, selectedQuestionId, isSinglePane, initialQuestionId, didAutoSelect]);

    const currentIndex = useMemo(() => {
        if (!selectedQuestionId) return -1;
        return filteredQuestions.findIndex(q => q.id === selectedQuestionId);
    }, [selectedQuestionId, filteredQuestions]);

    // ── Callbacks ──────────────────────────────────────────────────────
    const handleFetch = useCallback(() => {
        setAppliedFilters({ ...filters });
    }, [filters]);

    const goToPrevQuestion = useCallback(() => {
        if (currentIndex > 0) {
            setSelectedQuestionId(filteredQuestions[currentIndex - 1].id);
            setIsEditing(false);
        }
    }, [currentIndex, filteredQuestions]);

    const goToNextQuestion = useCallback(() => {
        if (currentIndex < filteredQuestions.length - 1) {
            setSelectedQuestionId(filteredQuestions[currentIndex + 1].id);
            setIsEditing(false);
        }
    }, [currentIndex, filteredQuestions]);

    const onSelectQuestion = useCallback((id: string) => {
        setSelectedQuestionId(id);
        setIsEditing(false);
    }, []);

    const onBackToList = useCallback(() => {
        setSelectedQuestionId(null);
        setIsEditing(false);
    }, []);

    const toggleSelectQuestion = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleSelectAll = useCallback(() => {
        setSelectedIds(prev => {
            if (prev.size === filteredQuestions.length) return new Set();
            return new Set(filteredQuestions.map(q => q.id));
        });
    }, [filteredQuestions]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    /** Stem for a related question. Reuses this page's own detail endpoint, so
     *  a consumer wiring the Related Questions list supplies only the href. */
    const fetchRelatedQuestionText = useCallback(
        (id: string) => api.getQuestionDetail(id).then(q => q.questionText),
        [api],
    );

    const refreshDetail = useCallback(() => {
        setIsEditing(false);
    }, []);

    // ── Subject options + default ─────────────────────────────────────
    // allowedSubjects is honoured from QUESTION_SUBJECT_OPTIONS (core four + Geography):
    // a teacher passes their profile subjects, the admin console the whole list.
    // Without one the filter stays the core four.
    const subjectOptions = allowedSubjects
        ? QUESTION_SUBJECT_OPTIONS.filter(s => allowedSubjects.includes(s))
        : SUBJECT_OPTIONS;

    // Default subject to first option if not set
    useEffect(() => {
        if (!filters.subject && subjectOptions.length > 0) {
            const defaultSubject = subjectOptions[0];
            setFilters(prev => ({ ...prev, subject: defaultSubject }));
            setAppliedFilters(prev => ({ ...prev, subject: defaultSubject }));
        }
    }, [subjectOptions]);

    return (
        <div className={`questions-page split-layout${hasDetail ? ' has-detail' : ''}`}>
            <QuestionListPanel
                totalCount={data?.total}
                filteredCount={filteredQuestions.length}
                renderHeaderActions={renderHeaderActions}
                renderAboveFilters={renderAboveFilters}
                statusMessage={statusMessage}
                onClearStatus={onClearStatus}
                onStatusClick={onStatusClick}
                filters={filters}
                setFilter={setFilter}
                subjectOptions={subjectOptions}
                modulesBySubject={modulesBySubject}
                isDirty={isDirty}
                isFetching={isFetching}
                handleFetch={handleFetch}
                isAdmin={isAdmin}
                showStatusFilter={showStatusFilter}
                hideCatalogueFilters={hideCatalogueFilters}
                advancedOpen={advancedOpen}
                setAdvancedOpen={setAdvancedOpen}
                selectedDiagramLocations={selectedDiagramLocations}
                toggleDiagramLocation={toggleDiagramLocation}
                advancedFilterCount={advancedFilterCount}
                onClearAdvancedFilters={handleClearAdvancedFilters}
                allTagNames={allTagNames}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                tagQuestionSets={tagQuestionSets}
                questionTagNames={questionTagNames}
                selectedQuestionId={selectedQuestionId}
                onSelectQuestion={onSelectQuestion}
                selectedIds={selectedIds}
                toggleSelectQuestion={toggleSelectQuestion}
                toggleSelectAll={toggleSelectAll}
                renderSelectionBar={renderSelectionBar}
                clearSelection={clearSelection}
                isLoading={isLoading}
                filteredQuestions={filteredQuestions}
                modulesById={modulesById}
            />

            <QuestionDetailPanel
                selectedQuestionId={selectedQuestionId}
                selectedQuestion={selectedQuestion}
                isDetailLoading={isDetailLoading}
                isCreating={isCreating}
                isEditing={isEditing}
                onBackToList={onBackToList}
                currentIndex={currentIndex}
                totalCount={filteredQuestions.length}
                goToPrev={goToPrevQuestion}
                goToNext={goToNextQuestion}
                renderEditMode={renderEditMode}
                renderCreateMode={renderCreateMode}
                renderDetailActions={renderDetailActions}
                renderDetailLeftActions={renderDetailLeftActions}
                renderDetailBadges={renderDetailBadges}
                renderDetailExtra={renderDetailExtra}
                renderBeforeMoreDetails={renderBeforeMoreDetails}
                renderInMoreDetails={renderInMoreDetails}
                historyApi={historyApi}
                refreshDetail={refreshDetail}
                setIsEditing={setIsEditing}
                isAdmin={isAdmin}
                hideProvenance={hideProvenance}
                resolveTeacherName={resolveTeacherName}
                viewerId={viewerId}
                getModuleLabel={getModuleLabel}
                modulesBySubject={modulesBySubject}
                fetchQuestionText={fetchRelatedQuestionText}
                questionHref={relatedQuestionHref}
            />
        </div>
    );
}
